import { Single } from 'infrastructure/api'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import {
  platformFeaturesWithDefaultAtom,
  selectedActionAtom,
  selectedSchoolsAtom,
} from './platformFeatures.atoms'
import { useCallback, useMemo } from 'react'
import { FEATURE_PACKAGES } from './platformFeatures.utils'
import {
  addDistrictFeature,
  districtIdAtom,
  removeDistrictFeature,
} from 'shared/api/district'
import { addSchoolFeature, removeSchoolFeature } from 'shared/api/mutations'

function useSelectedFeatures(
  selectedFeatureName: string,
): Single.PlatformFeature[] {
  const platformFeatures = useAtomValue(platformFeaturesWithDefaultAtom)

  return useMemo(() => {
    const featureNames = FEATURE_PACKAGES[selectedFeatureName]

    if (!featureNames) return []

    const featureForName = (fn: string) =>
      platformFeatures.find(({ name }) => name === fn)

    return featureNames
      .map(featureForName)
      .filter(Boolean) as Single.PlatformFeature[]
  }, [selectedFeatureName, platformFeatures])
}

export function useFeaturePackageUpdate(selectedFeatureName: string) {
  const selectedAction = useAtomValue(selectedActionAtom)
  const selectedPlatformFeatures = useSelectedFeatures(selectedFeatureName)
  const selectedSchools = useAtomValue(selectedSchoolsAtom)

  const district_id = useAtomValue(districtIdAtom)

  return useCallback(async () => {
    if (!selectedAction || !selectedPlatformFeatures) return

    const { entityType, actionType, locked } = selectedAction

    const promises = selectedPlatformFeatures.map(({ id: feature_id }) => {
      if (entityType === 'district' && district_id) {
        if (actionType === 'remove') {
          return removeDistrictFeature({ district_id, feature_id })
        } else {
          return addDistrictFeature({
            district_id,
            feature_id,
            locked: !!locked,
          })
        }
      } else {
        const schoolIds = selectedSchools.map(({ id }) => Number(id))
        if (actionType === 'remove') {
          return removeSchoolFeature(schoolIds, feature_id)
        } else {
          return addSchoolFeature(schoolIds, feature_id)
        }
      }
    })
    await Promise.all(promises)
  }, [selectedAction, selectedPlatformFeatures, district_id, selectedSchools])
}
